import { TOV_VERSION_STATUS } from "contracts/enums/tov";
import { TOV_MAP_ENQUEUE_OUTCOME } from "contracts/enums/tov-mapping";
import { Show } from "solid-js";
import { api } from "../../store/api";
import type { useBrandAuthoring } from "../scan/useBrandAuthoring";
import { AddTraitForm } from "./AddTraitForm";
import { ProfileBar, VersionSwitcher } from "./AuthorForms";
import { EvidencePanel } from "./EvidencePanel";
import {
  checkpointProgressLabel,
  isMappingFailed,
  mapActionLabel,
} from "./mapAttemptState";
import { SectionTabs } from "./SectionTabs";
import { requestMapping } from "./useMapAttempt";
import { VersionSpine } from "./VersionSpine";

type Authoring = ReturnType<typeof useBrandAuthoring>;

/**
 * The Client-scoped TOV authoring body: upload, profile/version switching, the
 * definition section tabs, evidence, and trait authoring. Parameterised only by
 * the authoring state, so it mounts inside the Signal Scan shell and the
 * playground drawer alike. Edit controls render for draft versions only — the
 * backend rejects edits to active/archived versions.
 */
export function TovAuthoringPanel(props: { authoring: Authoring }) {
  const a = props.authoring;
  return (
    <>
      <div class="tov-panel-head">
        <label
          class={`btn btn-primary tov-header-upload ${
            !a.clientId() || a.uploadBusy() ? "disabled" : ""
          }`}
        >
          <input
            type="file"
            disabled={!a.clientId() || a.uploadBusy()}
            onChange={a.onPick}
          />
          <span>{a.uploadBusy() ? "Uploading…" : "Upload document"}</span>
        </label>
      </div>
      <Show when={a.error()}>
        <div class="gate-error">{a.error()}</div>
      </Show>
      <Show
        when={a.clientId() && a.profiles.data}
        fallback={<div class="loading">Loading tone of voice…</div>}
      >
        <ProfileBar
          profiles={a.profiles.data?.profiles ?? []}
          activeVersionId={a.versionId()}
          onSelect={a.selectProfile}
          onCreateProfile={(name) =>
            a.guard(async () => {
              const created = await api.tov.createProfile(a.clientId() ?? "", {
                name,
              });
              a.setProfileId(created.id);
              a.setVersionId(created.currentVersionId);
            })
          }
          onCreateVersion={(targetProfileId, label, summary) =>
            a.guard(async () => {
              const created = await api.tov.createVersion(
                a.clientId() ?? "",
                targetProfileId,
                { label, summary },
              );
              a.seedVersion(created);
              a.setProfileId(targetProfileId);
              a.setVersionId(created.id);
            })
          }
        />
        <VersionSwitcher
          versions={a.versions.data?.versions ?? []}
          activeVersionId={a.versionId()}
          onSelect={(id) => a.setVersionId(id)}
        />
        <Show
          when={a.version.data}
          keyed
          fallback={<p class="tov-empty">No version to show yet.</p>}
        >
          {(loaded) => (
            <>
              <VersionSpine
                profileName={a.profileName()}
                version={loaded}
                onActivate={() =>
                  a.guard(async () => {
                    a.seedVersion(
                      await api.tov.activateVersion(
                        a.clientId() ?? "",
                        loaded.id,
                      ),
                    );
                  })
                }
                onMap={() =>
                  a.guard(async () => {
                    a.setMapNotice("");
                    a.setMapPending(true);
                    try {
                      const result = await requestMapping(
                        a.clientId() ?? "",
                        loaded.id,
                      );
                      if (
                        result.outcome === TOV_MAP_ENQUEUE_OUTCOME.COALESCED
                      ) {
                        a.setMapNotice(
                          "A mapping run is already in flight for this version — no second run was queued.",
                        );
                      }
                      if (result.outcome === TOV_MAP_ENQUEUE_OUTCOME.RESUMED) {
                        a.setMapNotice(
                          "Resuming the failed mapping run from its saved progress — already-extracted page groups are not re-run.",
                        );
                      }
                    } finally {
                      a.setMapPending(false);
                    }
                  })
                }
                mapping={a.mappingActive()}
                mapLabel={mapActionLabel(a.mapAttempt.state())}
              />
              <Show when={a.mapNotice()}>
                <p class="tov-sub" role="status">
                  {a.mapNotice()}
                </p>
              </Show>
              <Show
                when={
                  !a.mappingActive() && isMappingFailed(a.mapAttempt.state())
                }
              >
                <div class="gate-error" role="alert">
                  <strong>Mapping failed: </strong>
                  {a.mapAttempt.summary()?.attempt?.failureReason ??
                    "No failure detail was recorded."}
                  <Show when={checkpointProgressLabel(a.mapAttempt.summary())}>
                    {(progress) => <p class="tov-sub">{progress()}</p>}
                  </Show>
                </div>
              </Show>
              <SectionTabs
                clientId={a.clientId() ?? ""}
                version={loaded}
                edit={a.editProps(loaded.status === TOV_VERSION_STATUS.DRAFT)}
                active={a.activeTab()}
                onActiveChange={a.setActiveTab}
              />
              <EvidencePanel clientId={a.clientId() ?? ""} version={loaded} />
              <Show when={loaded.status === TOV_VERSION_STATUS.DRAFT}>
                <AddTraitForm
                  nextOrder={loaded.traits.length}
                  onAdd={(trait) =>
                    a.guard(async () => {
                      a.seedVersion(
                        await api.tov.addTrait(
                          a.clientId() ?? "",
                          loaded.id,
                          trait,
                        ),
                      );
                    })
                  }
                />
              </Show>
            </>
          )}
        </Show>
      </Show>
    </>
  );
}
